/**
 * One visit, from "press a product" to "take your product" (or a refund).
 *
 * Everything shown comes from the server's snapshot, and the buttons are
 * exactly its `allowed_actions`. The price is the one the MACHINE reported;
 * this page never sends an amount.
 */
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ActionKeys,
  NetworkError,
  cancelVisit,
  errorKey,
  openVisit,
  payVisit,
  refundVisit,
} from "../api";
import { ChannelPicker } from "../ChannelPicker";
import { AppBar, ErrorCard, Modal, Spinner, StatusIcon, Steps } from "../components";
import { useI18n } from "../i18n";
import { store } from "../storage";
import { useVisit } from "../useVisit";
import { TERMINAL, formatYen, isResumable, presentation, type Channel, type VendSession } from "../vend";

type Busy = null | "pay" | "cancel" | "refund" | "change";

export function VisitRoute() {
  const { id = "" } = useParams();
  // A new id is a new visit: start from a clean screen, not the old one's state.
  return <VisitScreen key={id} id={id} />;
}

function VisitScreen({ id }: { id: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as { flash?: string } | null)?.flash ?? null;
  const { visit, error, link, replace } = useVisit(id);
  const keys = useRef(new ActionKeys());
  const [busy, setBusy] = useState<Busy>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Back from the payment page (bfcache restores this page as it was left).
  useEffect(() => {
    const reset = () => setRedirecting(false);
    window.addEventListener("pageshow", reset);
    return () => window.removeEventListener("pageshow", reset);
  }, []);

  // Forget the visit once nothing more can happen to it.
  useEffect(() => {
    if (visit && !isResumable(visit) && store.get("visit") === visit.vend_session_id) {
      store.remove("visit");
      store.remove("qr");
    }
  }, [visit]);

  async function act(name: Exclude<Busy, null | "change">, call: (key: string) => Promise<VendSession>) {
    setBusy(name);
    setActionError(null);
    try {
      const v = await call(keys.current.for(name));
      keys.current.done(name);
      replace(v);
      return v;
    } catch (e) {
      // A dropped connection keeps the key: retrying the same action is safe.
      if (!(e instanceof NetworkError)) keys.current.done(name);
      setActionError(t(errorKey(e)));
      return null;
    } finally {
      setBusy(null);
    }
  }

  function goToPayment(v: VendSession) {
    const target = v.payment?.redirect_url ?? v.payment?.deeplink;
    if (!target) return;
    setRedirecting(true);
    // Same tab: the visit is remembered and resumes when the customer returns.
    window.location.assign(target);
  }

  async function pay() {
    const v = await act("pay", (key) => payVisit(id, key));
    if (v) goToPayment(v);
  }

  async function changeMethod(next: Channel) {
    setChangeOpen(false);
    if (!visit || next === visit.channel) return;
    store.set("channel", next);
    const qr = store.get("qr");
    setBusy("change");
    setActionError(null);
    try {
      await cancelVisit(id, keys.current.for("cancel"));
      keys.current.done("cancel");
      if (!qr) {
        navigate("/", { replace: true });
        return;
      }
      const action = `reopen:${next}`;
      const v = await openVisit(qr, next, keys.current.for(action));
      keys.current.done(action);
      store.set("visit", v.vend_session_id);
      navigate(`/visit/${encodeURIComponent(v.vend_session_id)}`, {
        replace: true,
        state: { flash: "vend.method.changed" },
      });
    } catch (e) {
      setActionError(t(errorKey(e)));
    } finally {
      setBusy(null);
    }
  }

  function goHome() {
    if (visit && !isResumable(visit)) {
      store.remove("visit");
      store.remove("qr");
    }
    navigate("/");
  }

  if (!visit) {
    return (
      <div className="aseed-shell">
        <AppBar onHome={goHome} />
        <main className="aseed-shell__main">
          <div className="aseed-shell__content v-stack">
            {error ? (
              <>
                <ErrorCard>{t(errorKey(error))}</ErrorCard>
                <button
                  type="button"
                  className="aseed-button v-wide"
                  onClick={() => {
                    store.remove("visit");
                    store.remove("qr");
                    navigate("/");
                  }}
                >
                  {t("vend.home")}
                </button>
              </>
            ) : (
              <div className="v-center">
                <Spinner label={t("common.loading")} />
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  const { tone, step } = presentation(visit.state);
  const can = new Set(visit.allowed_actions);
  const amount = typeof visit.amount_yen === "number" ? formatYen(visit.amount_yen, locale) : null;
  const method = t(`payment.channel.${visit.channel}`);
  const canChangeMethod = can.has("cancel") && (visit.state === "opened" || visit.state === "selected");
  const showReference = tone === "problem" || visit.state === "refunded";
  const finished = TERMINAL.has(visit.state) && !can.has("request_refund");
  const disabled = busy !== null || redirecting;

  return (
    <div className="aseed-shell">
      <AppBar onHome={goHome} />
      <main className="aseed-shell__main">
        <div className="aseed-shell__content v-stack">
          {visit.state !== "canceled" && visit.state !== "expired" && visit.state !== "refunded" ? (
            <Steps current={step} problem={tone === "problem"} />
          ) : null}

          <section className={`v-status v-status--${tone}`} aria-live="polite">
            <StatusIcon tone={tone} />
            <h1 className="v-status__title">{t(`vend.state.${visit.state}.title`)}</h1>
            <p className="v-status__hint">{t(`vend.state.${visit.state}.hint`)}</p>
          </section>

          {flash ? <div className="aseed-status-banner v-note">{t(flash)}</div> : null}
          {visit.unfulfilled && visit.state !== "unknown" ? (
            <div className="aseed-status-banner aseed-status-banner--unfulfilled">{t("operation.unfulfilled")}</div>
          ) : null}
          {actionError ? <ErrorCard>{actionError}</ErrorCard> : null}
          {link === "reconnecting" ? <p className="v-muted v-small v-center">{t("vend.live.reconnecting")}</p> : null}

          <section className="v-card v-summary">
            <div className="v-summary__row">
              <span className="v-muted">{t("vend.machine", { id: "" }).trim()}</span>
              <span className="v-summary__value">{visit.machine_id}</span>
            </div>
            {typeof visit.grid_no === "number" ? (
              <div className="v-summary__row">
                <span className="v-muted">{t("vend.slot", { slot: "" }).trim()}</span>
                <span className="v-summary__value">{visit.grid_no}</span>
              </div>
            ) : null}
            <div className="v-summary__row">
              <span className="v-muted">{t("vend.method")}</span>
              <span className="v-summary__value">
                {method}
                {canChangeMethod ? (
                  <button type="button" className="v-link" disabled={disabled} onClick={() => setChangeOpen(true)}>
                    {t("vend.method.change")}
                  </button>
                ) : null}
              </span>
            </div>
            <div className="aseed-amount aseed-amount--total v-summary__total">
              <span className="aseed-amount__label">{t("vend.price")}</span>
              <span className="aseed-amount__value">{amount ?? "—"}</span>
            </div>
            {showReference ? (
              <div className="v-summary__row v-reference">
                <span className="v-muted">{t("vend.reference")}</span>
                <span className="v-summary__value v-mono">{visit.vend_session_id}</span>
              </div>
            ) : null}
          </section>
        </div>
      </main>

      <div className="aseed-sticky-actions v-actions">
        {can.has("pay") ? (
          <button
            type="button"
            className="aseed-button v-wide"
            disabled={disabled || amount === null}
            onClick={() => void pay()}
          >
            {busy === "pay" || redirecting ? <Spinner /> : null}
            {amount ? t("vend.pay", { method, amount }) : t("vend.state.opened.title")}
          </button>
        ) : null}

        {can.has("open_payment") && visit.payment?.redirect_url ? (
          <a className="aseed-button v-wide v-button-link" href={visit.payment.redirect_url}>
            {t("vend.openPayment")}
          </a>
        ) : null}
        {can.has("open_payment") && visit.payment?.deeplink ? (
          <a className="aseed-button v-wide v-button-link v-button-ghost" href={visit.payment.deeplink}>
            {t("vend.openApp")}
          </a>
        ) : null}

        {can.has("request_refund") ? (
          <button
            type="button"
            className="aseed-button aseed-button--danger v-wide"
            disabled={disabled}
            onClick={() => void act("refund", (key) => refundVisit(id, key))}
          >
            {busy === "refund" ? <Spinner /> : null}
            {t("vend.refund")}
          </button>
        ) : null}

        {can.has("cancel") ? (
          <button
            type="button"
            className="v-link v-wide"
            disabled={disabled}
            onClick={() => setConfirmCancel(true)}
          >
            {t("vend.cancel")}
          </button>
        ) : null}

        {finished ? (
          <button type="button" className="aseed-button v-wide" onClick={goHome}>
            {t("vend.again")}
          </button>
        ) : null}
      </div>

      <Modal open={confirmCancel} title={t("vend.cancel.confirm.title")} onClose={() => setConfirmCancel(false)}>
        <p>{t("vend.cancel.confirm.body")}</p>
        <div className="v-modal__actions">
          <button type="button" className="aseed-button" onClick={() => setConfirmCancel(false)}>
            {t("vend.cancel.confirm.keep")}
          </button>
          <button
            type="button"
            className="aseed-button aseed-button--cancel"
            onClick={() => {
              setConfirmCancel(false);
              void act("cancel", (key) => cancelVisit(id, key));
            }}
          >
            {t("vend.cancel.confirm.stop")}
          </button>
        </div>
      </Modal>

      <Modal open={changeOpen} title={t("vend.method")} onClose={() => setChangeOpen(false)}>
        <p className="v-muted v-small">{t("vend.method.changeNote")}</p>
        <ChannelPicker value={visit.channel} onChange={(c) => void changeMethod(c)} />
        <div className="v-modal__actions">
          <button type="button" className="v-link" onClick={() => setChangeOpen(false)}>
            {t("common.close")}
          </button>
        </div>
      </Modal>

      {busy === "change" || redirecting ? (
        <div className="v-busy" role="status" aria-live="polite">
          <Spinner />
          <p>{t(redirecting ? "vend.redirecting" : "vend.opening")}</p>
        </div>
      ) : null}
    </div>
  );
}
